import moment from 'moment'
import * as XLSX from 'xlsx/xlsx.mjs'

export class ExcelExporter {
    static async exportToExcel(data, columns, fileName, sheetName = 'Report') {
        try {
            const formattedData = data.map(item => {
                const formattedItem = {}

                columns.forEach(column => {
                    const value = this.getNestedValue(item, column.key)
                    const formattedValue = this.formatValueForExcel(
                        value,
                        column.type,
                        column
                    )
                    formattedItem[column.label] = formattedValue
                })

                return formattedItem
            })

            const worksheet = XLSX.utils.json_to_sheet(formattedData)

            await this.addLinksToWorksheet(worksheet, data, columns)

            const columnWidths = columns.map(column => ({
                wch: column.width || 15,
            }))
            worksheet['!cols'] = columnWidths

            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

            const finalFileName = `${fileName}.xlsx`

            XLSX.writeFile(workbook, finalFileName)

            return true
        } catch (error) {
            throw new Error(`Error exporting to Excel: ${error.message}`)
        }
    }

    static getNestedValue(obj, path) {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined
                ? current[key]
                : undefined
        }, obj)
    }

    static formatValueForExcel(value, type) {
        if (value === null || value === undefined) {
            return ''
        }

        switch (type) {
            case 'date':
                if (value.seconds) {
                    return moment.unix(value.seconds).format('YYYY-MM-DD')
                }
                if (value._seconds) {
                    return moment.unix(value._seconds).format('YYYY-MM-DD')
                }
                return moment(value).format('YYYY-MM-DD')

            case 'number':
                return typeof value === 'number'
                    ? value
                    : parseFloat(value) || 0

            case 'currency':
                return typeof value === 'number'
                    ? new Intl.NumberFormat('de-DE').format(Math.ceil(value))
                    : value

            case 'boolean':
                return value === true ? 'Yes' : 'No'

            case 'array':
                if (Array.isArray(value) && value.length > 0) {
                    const validItems = value
                        .filter(item => {
                            if (
                                typeof item === 'object' &&
                                (item.consecutive || item.fileName)
                            ) {
                                return item.consecutive || item.fileName
                            }
                            return false
                        })
                        .map(item => item.consecutive || item.fileName)

                    return validItems.length > 0
                        ? validItems.join(', ')
                        : "doesn't have invoices"
                }
                return "doesn't have invoices"

            case 'object':
                if (value && typeof value === 'object') {
                    if (value.name) return value.name
                    if (value.fileName) return value.fileName
                    return JSON.stringify(value)
                }
                return value.toString()

            case 'link':
                if (Array.isArray(value) && value.length > 0) {
                    const validItems = value
                        .filter(item => {
                            if (
                                typeof item === 'object' &&
                                (item.consecutive || item.fileName)
                            ) {
                                return item.consecutive || item.fileName
                            }
                            return false
                        })
                        .map(item => item.consecutive || item.fileName)

                    return validItems.length > 0
                        ? validItems.join(', ')
                        : "doesn't have invoices"
                }
                return "doesn't have invoices"

            default:
                return value.toString()
        }
    }

    static async addLinksToWorksheet(worksheet, data, columns) {
        const invoicesColumn = columns.find(col => col.key === 'invoices')
        if (!invoicesColumn) return

        const invoicesColumnIndex = columns.findIndex(
            col => col.key === 'invoices'
        )

        const issueDateColumnIndex = columns.findIndex(
            col => col.key === 'invoices_issue_date'
        )

        const dueDateColumnIndex = columns.findIndex(
            col => col.key === 'invoices_due_date'
        )

        let maxInvoices = 0
        data.forEach(item => {
            if (item.invoices && item.invoices.length > 0) {
                const validInvoices = item.invoices.filter(
                    invoice =>
                        invoice &&
                        (invoice.consecutive || invoice.fileName) &&
                        (invoice.consecutive?.trim() !== '' ||
                            invoice.fileName?.trim() !== '')
                )
                maxInvoices = Math.max(maxInvoices, validInvoices.length)
            }
        })

        // Generar columnas dinámicas basadas en el número real de invoices
        if (maxInvoices > 0) {
            const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1')

            // Generar columnas dinámicas: Invoice 2, Issue Date 2, Due Date 2, Invoice 3, Issue Date 3, Due Date 3, etc.
            // (Invoice 1, Issue Date 1 y Due Date 1 ya están definidos en la configuración inicial)
            for (let i = 1; i < maxInvoices; i++) {
                // Columna para Invoice adicional
                const invoiceColumnIndex = invoicesColumnIndex + i * 3
                const invoiceColumnLetter = XLSX.utils.encode_col(
                    invoiceColumnIndex
                )
                const invoiceHeaderCell = `${invoiceColumnLetter}1`

                worksheet[invoiceHeaderCell] = {
                    v: `Invoice ${i + 1}`,
                    t: 's',
                }

                // Columna para Issue Date adicional
                const currentIssueDateColumnIndex = invoicesColumnIndex + i * 3 + 1
                const issueDateColumnLetter = XLSX.utils.encode_col(
                    currentIssueDateColumnIndex
                )
                const issueDateHeaderCell = `${issueDateColumnLetter}1`

                worksheet[issueDateHeaderCell] = {
                    v: `Issue Date ${i + 1}`,
                    t: 's',
                }

                // Columna para Due Date adicional
                const currentDueDateColumnIndex = invoicesColumnIndex + i * 3 + 2
                const dueDateColumnLetter = XLSX.utils.encode_col(
                    currentDueDateColumnIndex
                )
                const dueDateHeaderCell = `${dueDateColumnLetter}1`

                worksheet[dueDateHeaderCell] = {
                    v: `Due Date ${i + 1}`,
                    t: 's',
                }
            }

            // Actualizar el rango para incluir todas las columnas generadas
            range.e.c = Math.max(
                range.e.c,
                invoicesColumnIndex + maxInvoices * 3 - 1
            )
            worksheet['!ref'] = XLSX.utils.encode_range(range)

            if (!worksheet['!cols']) {
                worksheet['!cols'] = new Array(range.e.c + 1).fill(null)
            }

            // Configurar ancho de columnas para todas las columnas generadas
            for (let i = 0; i < maxInvoices; i++) {
                let invoiceColumnIndex, currentIssueDateColumnIndex, currentDueDateColumnIndex

                if (i === 0) {
                    // Primera factura: usar las columnas originales
                    invoiceColumnIndex = invoicesColumnIndex
                    currentIssueDateColumnIndex = issueDateColumnIndex
                    currentDueDateColumnIndex = dueDateColumnIndex
                } else {
                    // Facturas adicionales: usar la fórmula de columnas dinámicas
                    invoiceColumnIndex = invoicesColumnIndex + i * 3
                    currentIssueDateColumnIndex = invoicesColumnIndex + i * 3 + 1
                    currentDueDateColumnIndex = invoicesColumnIndex + i * 3 + 2
                }

                worksheet['!cols'][invoiceColumnIndex] = { wch: 25 } // Ancho para Invoice
                worksheet['!cols'][currentIssueDateColumnIndex] = { wch: 15 } // Ancho para Issue Date
                worksheet['!cols'][currentDueDateColumnIndex] = { wch: 15 } // Ancho para Due Date
            }
        }

        for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
            const item = data[rowIndex]
            const excelRowIndex = rowIndex + 2

            if (item.invoices && item.invoices.length > 0) {
                const validInvoices = item.invoices.filter(
                    invoice =>
                        invoice &&
                        (invoice.consecutive || invoice.fileName) &&
                        (invoice.consecutive?.trim() !== '' ||
                            invoice.fileName?.trim() !== '')
                )

                // Llenar TODAS las columnas posibles (basadas en maxInvoices global)
                for (
                    let invoiceNumber = 1;
                    invoiceNumber <= maxInvoices;
                    invoiceNumber++
                ) {
                    // Calcular índices de columnas basados en el número de invoice (1, 2, 3, 4...)
                    let invoiceColumnIndex, currentIssueDateColumnIndex, currentDueDateColumnIndex

                    if (invoiceNumber === 1) {
                        // Primera factura: usar las columnas originales
                        invoiceColumnIndex = invoicesColumnIndex
                        currentIssueDateColumnIndex = issueDateColumnIndex
                        currentDueDateColumnIndex = dueDateColumnIndex
                    } else {
                        // Facturas adicionales: usar la fórmula de columnas dinámicas
                        invoiceColumnIndex =
                            invoicesColumnIndex + (invoiceNumber - 1) * 3
                        currentIssueDateColumnIndex =
                            invoicesColumnIndex + (invoiceNumber - 1) * 3 + 1
                        currentDueDateColumnIndex =
                            invoicesColumnIndex + (invoiceNumber - 1) * 3 + 2
                    }

                    const invoiceColumnLetter = XLSX.utils.encode_col(
                        invoiceColumnIndex
                    )
                    const invoiceCellAddress = `${invoiceColumnLetter}${excelRowIndex}`

                    const issueDateColumnLetter = XLSX.utils.encode_col(
                        currentIssueDateColumnIndex
                    )
                    const issueDateCellAddress = `${issueDateColumnLetter}${excelRowIndex}`

                    const dueDateColumnLetter = XLSX.utils.encode_col(
                        currentDueDateColumnIndex
                    )
                    const dueDateCellAddress = `${dueDateColumnLetter}${excelRowIndex}`

                    // Convertir invoiceNumber (1,2,3,4) a array index (0,1,2,3)
                    const arrayIndex = invoiceNumber - 1

                    // Llenar celda de Invoice
                    if (arrayIndex < validInvoices.length) {
                        const invoice = validInvoices[arrayIndex]
                        const displayName =
                            invoice.consecutive || invoice.fileName

                        // Only create hyperlink if fileName exists
                        if (
                            invoice.fileName &&
                            invoice.fileName.trim() !== ''
                        ) {
                            try {
                                const linkUrl = await this.generateFirebaseStorageUrl(
                                    item.companyId || 'xFCsAZPU4PoOgll12MQO',
                                    item.requestId,
                                    invoice.fileName
                                )

                                worksheet[invoiceCellAddress] = {
                                    v: displayName,
                                    l: {
                                        Target: linkUrl,
                                        Tooltip: displayName,
                                    },
                                    t: 's',
                                }
                            } catch (error) {
                                worksheet[invoiceCellAddress] = {
                                    v: displayName,
                                    t: 's',
                                }
                            }
                        } else {
                            // If no fileName, just display the consecutive without hyperlink
                            worksheet[invoiceCellAddress] = {
                                v: displayName,
                                t: 's',
                            }
                        }
                    } else {
                        // Si no hay invoice para este número, llenar con mensaje por defecto
                        worksheet[invoiceCellAddress] = {
                            v: "doesn't have invoices",
                            t: 's',
                        }
                    }

                    // Llenar celda de Issue Date (SIEMPRE, para todas las columnas)
                    if (arrayIndex < validInvoices.length) {
                        const invoice = validInvoices[arrayIndex]
                        // Si hay invoice, usar su invoicesDate o "-" si no tiene
                        const issueDate = invoice.invoicesDate
                            ? invoice.invoicesDate.split('T')[0]
                            : '-'
                        worksheet[issueDateCellAddress] = {
                            v: issueDate,
                            t: 's',
                        }
                    } else {
                        // Si no hay invoice para este número, llenar con "-"
                        worksheet[issueDateCellAddress] = {
                            v: '-',
                            t: 's',
                        }
                    }

                    // Llenar celda de Due Date (SIEMPRE, para todas las columnas)
                    if (arrayIndex < validInvoices.length) {
                        const invoice = validInvoices[arrayIndex]
                        // Si hay invoice, usar su dueDate o "-" si no tiene
                        worksheet[dueDateCellAddress] = {
                            v: invoice.dueDate || '-',
                            t: 's',
                        }
                    } else {
                        // Si no hay invoice para este número, llenar con "-"
                        worksheet[dueDateCellAddress] = {
                            v: '-',
                            t: 's',
                        }
                    }
                }
            } else {
                // Si no hay invoices, llenar todas las columnas con el mensaje por defecto
                for (
                    let invoiceNumber = 1;
                    invoiceNumber <= maxInvoices;
                    invoiceNumber++
                ) {
                    let invoiceColumnIndex, currentIssueDateColumnIndex, currentDueDateColumnIndex

                    if (invoiceNumber === 1) {
                        // Primera factura: usar las columnas originales
                        invoiceColumnIndex = invoicesColumnIndex
                        currentIssueDateColumnIndex = issueDateColumnIndex
                        currentDueDateColumnIndex = dueDateColumnIndex
                    } else {
                        // Facturas adicionales: usar la fórmula de columnas dinámicas
                        invoiceColumnIndex =
                            invoicesColumnIndex + (invoiceNumber - 1) * 3
                        currentIssueDateColumnIndex =
                            invoicesColumnIndex + (invoiceNumber - 1) * 3 + 1
                        currentDueDateColumnIndex =
                            invoicesColumnIndex + (invoiceNumber - 1) * 3 + 2
                    }

                    const invoiceColumnLetter = XLSX.utils.encode_col(
                        invoiceColumnIndex
                    )
                    const invoiceCellAddress = `${invoiceColumnLetter}${excelRowIndex}`

                    const issueDateColumnLetter = XLSX.utils.encode_col(
                        currentIssueDateColumnIndex
                    )
                    const issueDateCellAddress = `${issueDateColumnLetter}${excelRowIndex}`

                    const dueDateColumnLetter = XLSX.utils.encode_col(
                        currentDueDateColumnIndex
                    )
                    const dueDateCellAddress = `${dueDateColumnLetter}${excelRowIndex}`

                    worksheet[invoiceCellAddress] = {
                        v: "doesn't have invoices",
                        t: 's',
                    }
                    worksheet[issueDateCellAddress] = {
                        v: '-',
                        t: 's',
                    }
                    worksheet[dueDateCellAddress] = {
                        v: '-',
                        t: 's',
                    }
                }
            }
        }
    }

    static async generateFirebaseStorageUrl(companyId, requestId, fileName) {
        try {
            const { storage } = await import('@/services/firebase')
            const fullPath = `${companyId}/purchases/${requestId}/invoices/${fileName}`
            const storageRef = storage().ref(fullPath)

            const downloadUrl = await storageRef.getDownloadURL()
            return downloadUrl
        } catch (error) {
            throw new Error(
                `Error generating Firebase Storage URL: ${error.message}`
            )
        }
    }

    static formatNumber(number) {
        return new Intl.NumberFormat('de-DE').format(
            Math.ceil(number != undefined ? number : 0)
        )
    }
}

export async function exportPurchaseDataToExcel(
    data,
    fileName = 'Purchase_Data'
) {
    const columns = [
        { key: 'requestNumber', label: 'ID', type: 'string', width: 10 },
        { key: 'project.name', label: 'Project', type: 'string', width: 25 },
        { key: 'description', label: 'Item', type: 'string', width: 30 },
        { key: 'quantity', label: 'Quantity', type: 'number', width: 12 },
        { key: 'totalReceived', label: 'Received', type: 'number', width: 12 },
        {
            key: 'purchaseOrder.orderNumber',
            label: 'Order Number',
            type: 'string',
            width: 15,
        },
        {
            key: 'totalPrice',
            label: 'Total Price',
            type: 'currency',
            width: 15,
        },
        { key: 'supplierName', label: 'Supplier', type: 'string', width: 20 },
        { key: 'sap', label: 'SAP', type: 'boolean', width: 12 },
        { key: 'accountant', label: 'Accountant', type: 'boolean', width: 12 },
        { key: 'invoices', label: 'Invoice 1', type: 'link', width: 25 },
        {
            key: 'invoices_issue_date',
            label: 'Issue Date 1',
            type: 'string',
            width: 15,
        },
        {
            key: 'invoices_due_date',
            label: 'Due Date 1',
            type: 'string',
            width: 15,
        },
    ]

    return await ExcelExporter.exportToExcel(
        data,
        columns,
        fileName,
        'Purchase History'
    )
}
